import React, {
  useState, useMemo, useContext, useCallback
} from 'react';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import FlightIcon from '@material-ui/icons/Flight';
import Popper from '@material-ui/core/Popper';
import Fade from '@material-ui/core/Fade';
import isEmpty from 'lodash.isempty';
import { withTranslation } from 'react-i18next';
import styled, { ThemeProvider } from 'styled-components';
import CountryContext from 'context/CountryContext';
import theme, { media } from '../../../theme/styled-theme';

const SearchBar = styled.div`
  cursor: pointer;
  display: flex;
  flex: 1 0 0;
  position: relative;
  padding: 10px 14px;
  outline: 0;
  justify-content: space-between;
  align-items: center;
  background: white;
  font-family: ${props => props.theme.fontFamilyLato};;
  font-size: 16px;
  color: $greyLight75;
  font-weight: 300;
  border-radius: 4px;
  border: 1px solid rgb(230, 230, 230);
  box-shadow: rgba(0, 0, 0, 0.05) 0px 2px 13px 0px;

  ${media.forPhoneOnly`
    padding: 10px 14px;
    margin-right: 0;
  `}
`;

const DropdownLabel = styled.div`
  display: flex;
  align-items: center;
  padding: 0;
  overflow: hidden;
  white-space: nowrap;
`;

const Bracket = styled.span`
  height: 24px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transform: ${props => props.visible ? 'rotate(-180deg)' : 'rotate(0)'};
`;

const AirportContainer = styled.div`
  position: absolute;
  background: white;
  border: 1px solid $greyLight60;
  box-shadow: 0 6px 18px 0 rgba(0,0,0,0.20);
  border-radius: 4px;
  top: 0;
  left: 16px;
  margin-left: -14px;
  text-align: left;
  margin-top: 8px;
  padding: 1.125rem 0;
  z-index: 100;

  ${media.forPhoneOnly`
    overflow: scroll;
  `}
`;

const AirportIcon = styled.span`
  margin-right: 8px;
`;

const FieldText = styled.span`
  max-width: 94px;
  color: ${props => props.hasValue ? props.theme.greyLight200 : props.theme.greyLight75};
  font-weight: ${props => props.hasValue ? '700' : '400'};
  ${media.forPhoneOnly`
    max-width: 100%;
  `}
}
`;

const AIRPORTS = {
  tw: {
    TPE_TERMINAL_2: {
      name: 'Taoyuan Internationl Airport',
      place_id: 'ChIJ1RXSYsCfQjQRCbG1qZC2o3A',
      geometry: {
        lat: '25.078294',
        lng: '121.2289283',
      }
    },
    KAOHSIUNG: {
      name: 'Kaohsiung International Airport',
      place_id: 'ChIJmbv2P84cbjQRKdFFacSu6hw',
      geometry: {
        lat: '22.574629',
        lng: '120.3426181',
      }
    },
    TAICHUNG: {
      name: 'Taichung International Airport',
      place_id: 'ChIJcQOEX1MRaTQRJFCKXjMQZFg',
      geometry: {
        lat: '24.2540339',
        lng: '120.5979851',
      }
    },
    HUALIEN: {
      name: 'Hualien Airport',
      place_id: 'ChIJbem3tRGeaDQRPp_Kh6Hj6IQ',
      geometry: {
        lat: '24.0250114',
        lng: '121.6138648',
      }
    },
  },
  th: {
    DMK: {
      name: 'Don Mueang International Airport',
      place_id: 'ChIJLaasHYiC4jARoP8LPbMAAQ8',
      geometry: {
        lat: '13.9132602',
        lng: '100.60201',
      }
    },
    BKK: {
      name: 'Suvarnabhumi International Airport',
      place_id: 'ChIJTydCFXdnHTERB3oVT1UZDRI',
      geometry: {
        lat: '13.6912917',
        lng: '100.7487498',
      }
    }
  }
};

const AirportDropdown = props => {
  const {
    selectAirportAirport,
    t,
    selectedAirport,
  } = props;
  // eslint-disable-next-line no-unused-vars
  const country = useContext(CountryContext);
  const [anchorEl, setAnchorEl] = useState();
  const { code = 'tw' } = country;
  const COUNTRY_AIRPORTS = AIRPORTS[code];

  const handleClose = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();
    setTimeout(() => setAnchorEl(null), 0); // prevet calling it before onClickHandler
  }, []);

  const onClickHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      setAnchorEl(anchorEl ? null : e.currentTarget);
    },
    [anchorEl],
  );

  const renderAirportLocations = useMemo(() => (
    Object.keys(COUNTRY_AIRPORTS).map(key => {
      const { name: airportName } = COUNTRY_AIRPORTS[key];
      return (
        <MenuItem
          key={airportName}
          value={COUNTRY_AIRPORTS[key]}
          data-cy={`cy-airport-${key}`}
          onClick={() => selectAirportAirport(COUNTRY_AIRPORTS[key])}
        >
          <AirportIcon>
            <FlightIcon />
          </AirportIcon>
          {airportName}
        </MenuItem>
      );
    })
  ), []);

  const { name } = selectedAirport;
  const isAirportEmpty = isEmpty(selectedAirport);
  const popperConfig = useMemo(() => ({
    flip: {
      enabled: true,
    },
    preventOverflow: {
      enabled: true,
      boundariesElement: 'scrollParent',
      padding: 0,
    },
    arrow: {
      enabled: false,
    },
  }), []);

  const visible = Boolean(anchorEl);
  return (
    <ThemeProvider theme={theme}>
      <SearchBar
        data-cy="cy-airport-dropdown"
        onClick={onClickHandler}
      >
        <DropdownLabel>
          <AirportIcon>
            <FlightIcon />
          </AirportIcon>
          <FieldText
            hasValue={!isAirportEmpty}
            data-cy="cy-airport-selection"
          >
            {
              name || <span>{t('SEARCHBAR.TAB.AIRPORT.SELECT_AIRPORT')}</span>
            }
          </FieldText>
        </DropdownLabel>
        <Bracket visible={visible}>
          <KeyboardArrowDownIcon />
        </Bracket>

        <Popper
          placement="bottom-start"
          open={visible}
          anchorEl={anchorEl}
          modifiers={popperConfig}
          transition
          style={{ zIndex: 1001 }}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClose}>
              <Fade {...TransitionProps} timeout={150}>
                <AirportContainer>
                  <MenuList
                    width={256}
                    value={selectedAirport}
                  >
                    { renderAirportLocations }
                  </MenuList>
                </AirportContainer>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </SearchBar>
    </ThemeProvider>
  );
};

AirportDropdown.propTypes = {
  t: PropTypes.func.isRequired,
  selectAirportAirport: PropTypes.func.isRequired,
  selectedAirport: PropTypes.objectOf(PropTypes.any),
};

AirportDropdown.defaultProps = {
  selectedAirport: null,
};

export default compose(
  withTranslation(['common']),
  withRouter,
)(AirportDropdown);
